import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Scissors, SlidersHorizontal } from 'lucide-react';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { absUrl, errMsg, mtvMix, mtvStems } from '../../../lib/aiStudioApi';
import { hasStems } from './mtvSteps';
import { notReady, ServiceNote, useStudioServices } from '../useStudioServices';

const box = 'rounded-xl border border-neutral-800 bg-neutral-900/40 p-5';

function Level({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px] text-neutral-400"><span>{label}</span><span className="font-mono text-amber-200">{value}%</span></div>
      <Slider min={0} max={150} step={5} value={[value]} onValueChange={([v]) => onChange(v)} data-testid={testId} />
    </div>
  );
}

/** Stem split: vocals + instrumental, needed for the singer swap and the vocal/music balance. */
export function StemsCard({ project, onChanged }) {
  const stems = hasStems(project) ? project.mtv.stems : null;
  const down = notReady(useStudioServices(), ['stems', 'files']);
  const job = project.mtv_job || {};
  const running = job.status === 'running';
  const mine = /Splitting|separated|Mixing|mix ready/i.test(job.step || '');
  const [mix, setMix] = useState({ vocals: 100, instrumental: 100 });
  const usd = Math.max(0.01, (project.song_seconds || 180) * 0.0007).toFixed(2);
  const go = (fn, ok) => fn().then(() => { toast.success(ok); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className={box} data-testid="mtv-stems-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Scissors size={15} className="text-amber-300" /> Split vocals <span className="font-normal text-neutral-500">(optional)</span></div>
      <p className="mb-3 text-xs text-neutral-400">A clean studio separation into vocals and instrumental. Needed to swap the singer or rebalance vocals and music. One-time cost for this song: <span className="text-amber-200">≈ ${usd}</span>.</p>
      <ServiceNote svc={down} testId="mtv-stems-unavailable" />
      {mine && (running || job.status === 'failed') && (
        <div className={job.status === 'failed' ? 'mb-3 rounded-md border border-red-400/30 bg-red-500/5 px-3 py-2 text-[11px] text-red-200' : 'mb-3 flex items-center gap-2 text-[11px] text-neutral-300'}
          data-testid="mtv-stems-status">{running ? <><Loader2 size={12} className="animate-spin" /> {job.step}…</> : job.error}</div>
      )}
      {stems ? (
        <div className="space-y-3" data-testid="mtv-stems-ready">
          <div className="grid gap-2 sm:grid-cols-2">
            {[['Vocals', stems.vocals, 'vocals'], ['Instrumental', stems.instrumental, 'instrumental']].map(([l, u, k]) => (
              <div key={k} className="space-y-1 rounded-lg border border-neutral-800 p-2"><div className="text-[11px] text-neutral-300">{l}</div>
                <audio src={absUrl(u)} controls preload="none" className="h-7 w-full" data-testid={`mtv-stem-${k}`} /></div>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Level label="Vocals" value={mix.vocals} onChange={(v) => setMix({ ...mix, vocals: v })} testId="mtv-mix-vocals" />
            <Level label="Music" value={mix.instrumental} onChange={(v) => setMix({ ...mix, instrumental: v })} testId="mtv-mix-instrumental" />
          </div>
          <Button size="sm" variant="outline" disabled={running} onClick={() => go(() => mtvMix(project.id, { vocals: mix.vocals / 100, instrumental: mix.instrumental / 100 }), 'Mixing — it appears in the soundtrack list')}
            className="border-neutral-700 text-neutral-200" data-testid="mtv-make-mix-btn"><SlidersHorizontal size={13} className="mr-1" /> Make this mix · free</Button>
        </div>
      ) : (
        <Button size="sm" disabled={running || !project.music?.url || !!down} onClick={() => go(() => mtvStems(project.id), 'Splitting vocals — about a minute')}
          className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="mtv-split-stems-btn">
          {running ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Scissors size={13} className="mr-1" />} Split vocals · ≈ ${usd}
        </Button>
      )}
    </div>
  );
}
